import React from 'react';
import { useQuery } from '@tanstack/react-query';

interface BuildingStats {
  total: number;
  byType: Record<string, number>;
  byAccessibility: Record<string, number>;
  byElevator: Record<string, number>;
  byComplex: Record<
    string,
    {
      complexId: number;
      complexCode: number;
      total: number;
      byType: Record<string, number>;
    }
  >;
}

interface BuildingStatsResponse {
  message: string;
  data: BuildingStats;
}

const fetchBuildingStats = async (): Promise<BuildingStatsResponse> => {
  const response = await fetch('/api/buildings/stats');
  if (!response.ok) {
    throw new Error('Failed to fetch building statistics');
  }
  return response.json();
};

const BuildingStatsDashboard: React.FC = () => {
  const {
    data: statsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['buildingStats'],
    queryFn: fetchBuildingStats,
  });

  const getBuildingTypeLabel = (type: string) => {
    switch (type) {
      case 'corridor':
        return '복도식';
      case 'staircase':
        return '계단식';
      case 'general':
        return '일반식';
      case 'unclassified':
        return '미분류';
      default:
        return type;
    }
  };

  const getAccessibilityLabel = (accessibility: string) => {
    switch (accessibility) {
      case 'accessible':
        return '접근 가능';
      case 'restricted':
        return '제한적 접근';
      case 'inaccessible':
        return '출입 불가';
      case 'unclassified':
        return '미분류';
      default:
        return accessibility;
    }
  };

  const getBuildingTypeColor = (type: string) => {
    switch (type) {
      case 'corridor':
        return 'bg-blue-100 text-blue-800';
      case 'staircase':
        return 'bg-green-100 text-green-800';
      case 'general':
        return 'bg-purple-100 text-purple-800';
      case 'unclassified':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessibilityColor = (accessibility: string) => {
    switch (accessibility) {
      case 'accessible':
        return 'bg-green-100 text-green-800';
      case 'restricted':
        return 'bg-yellow-100 text-yellow-800';
      case 'inaccessible':
        return 'bg-red-100 text-red-800';
      case 'unclassified':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">동 유형 통계</h2>
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">동 유형 통계</h2>
        <div className="text-center py-8 text-red-500">
          통계 데이터를 불러오는 중 오류가 발생했습니다.
        </div>
      </div>
    );
  }

  const stats = statsData?.data;
  if (!stats) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-6">동 유형 통계</h2>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-600 mb-1">전체 동 수</h3>
          <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-600 mb-1">접근 가능</h3>
          <p className="text-2xl font-bold text-green-900">
            {stats.byAccessibility.accessible || 0}
          </p>
          <p className="text-xs text-green-600">
            (
            {calculatePercentage(
              stats.byAccessibility.accessible || 0,
              stats.total
            )}
            %)
          </p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-red-600 mb-1">출입 불가</h3>
          <p className="text-2xl font-bold text-red-900">
            {stats.byAccessibility.inaccessible || 0}
          </p>
          <p className="text-xs text-red-600">
            (
            {calculatePercentage(
              stats.byAccessibility.inaccessible || 0,
              stats.total
            )}
            %)
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-purple-600 mb-1">
            엘리베이터 있음
          </h3>
          <p className="text-2xl font-bold text-purple-900">
            {stats.byElevator.withElevator || 0}
          </p>
          <p className="text-xs text-purple-600">
            (
            {calculatePercentage(
              stats.byElevator.withElevator || 0,
              stats.total
            )}
            %)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Building Type Distribution */}
        <div>
          <h3 className="text-lg font-medium mb-4">동 유형별 분포</h3>
          <div className="space-y-3">
            {Object.entries(stats.byType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBuildingTypeColor(type)}`}
                  >
                    {getBuildingTypeLabel(type)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">{count}개</span>
                  <span className="text-xs text-gray-500">
                    ({calculatePercentage(count, stats.total)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Simple Bar Chart */}
          <div className="mt-4 space-y-2">
            {Object.entries(stats.byType).map(([type, count]) => (
              <div key={type} className="flex items-center space-x-2">
                <div className="w-20 text-xs text-gray-600 truncate">
                  {getBuildingTypeLabel(type)}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                  <div
                    className={`h-4 rounded-full ${getBuildingTypeColor(type).replace('text-', 'bg-').replace('100', '500')}`}
                    style={{
                      width: `${calculatePercentage(count, stats.total)}%`,
                    }}
                  ></div>
                </div>
                <div className="w-12 text-xs text-gray-600 text-right">
                  {calculatePercentage(count, stats.total)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Accessibility Distribution */}
        <div>
          <h3 className="text-lg font-medium mb-4">접근성별 분포</h3>
          <div className="space-y-3">
            {Object.entries(stats.byAccessibility).map(
              ([accessibility, count]) => (
                <div
                  key={accessibility}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAccessibilityColor(accessibility)}`}
                    >
                      {getAccessibilityLabel(accessibility)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{count}개</span>
                    <span className="text-xs text-gray-500">
                      ({calculatePercentage(count, stats.total)}%)
                    </span>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Simple Bar Chart */}
          <div className="mt-4 space-y-2">
            {Object.entries(stats.byAccessibility).map(
              ([accessibility, count]) => (
                <div
                  key={accessibility}
                  className="flex items-center space-x-2"
                >
                  <div className="w-20 text-xs text-gray-600 truncate">
                    {getAccessibilityLabel(accessibility)}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                    <div
                      className={`h-4 rounded-full ${getAccessibilityColor(accessibility).replace('text-', 'bg-').replace('100', '500')}`}
                      style={{
                        width: `${calculatePercentage(count, stats.total)}%`,
                      }}
                    ></div>
                  </div>
                  <div className="w-12 text-xs text-gray-600 text-right">
                    {calculatePercentage(count, stats.total)}%
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Complex-wise Statistics */}
      <div className="mt-8">
        <h3 className="text-lg font-medium mb-4">단지별 동 유형 현황</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  단지명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  전체
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  복도식
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  계단식
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  일반식
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  미분류
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(stats.byComplex).map(
                ([complexName, complexData]) => (
                  <tr key={complexName} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {complexName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {complexData.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {complexData.byType.corridor || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {complexData.byType.staircase || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {complexData.byType.general || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {complexData.byType.unclassified || 0}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>

          {Object.keys(stats.byComplex).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              단지 데이터가 없습니다.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingStatsDashboard;
